import { useState, useRef } from 'react'
import useStyles from './styles'
import PropTypes from 'prop-types'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import useTranslations from '../../../i18n/useTranslations'
import { ReactComponent as IconUpload } from '../../../assets/icons/upload.svg'
import { ReactComponent as IconClose } from '../../../assets/icons/trash.svg'
import { ReactComponent as IconSuccesful } from '../../../assets/icons/FileTextSuccess.svg'
import { ReactComponent as IconError } from '../../../assets/icons/warning.svg'
import { generateRandomKey } from '../../../utils'
import { useUserContext } from '../../../auth/UserProvider'
import { useQueryClient } from 'react-query'
import { MD5 } from 'crypto-js'
import { useUploadFiles } from '../../../services/shippingsRecording'
import { useEffect } from 'react'

const FileUploaderRecording = ({
  height,
  maxFiles,
  acceptFiles,
  initialFiles,
  onAdd,
  onDelete,
  onLoad,
  onError,
}) => {
  const [files, setFiles] = useState(initialFiles ?? [])
  const [showAlert, setShowAlert] = useState()
  const inputRef = useRef()
  const t = useTranslations()
  const classes = useStyles()
  const queryClient = useQueryClient()

  const { clientId } = useUserContext()

  useEffect(() => {
    let timer
    if (showAlert !== null) {
      timer = setTimeout(() => {
        setShowAlert(null)
      }, 5000)
    }

    return () => clearTimeout(timer)
  }, [showAlert])

  const {
    mutate: uploadFile,
    isLoading: isLoadingFile,
    isError: isErrorFile,
  } = useUploadFiles(clientId)

  useEffect(() => {
    onLoad(isLoadingFile)
  }, [isLoadingFile, onLoad])

  const transformSize = (bytes, precision = 2) => {
    const kilo = 1024
    const megas = kilo ** 2
    const gigas = kilo ** 3
    const teras = kilo ** 4
    const petas = kilo ** 5

    if (bytes < kilo) {
      return bytes + ' B'
    } else if (bytes < megas) {
      return (bytes / kilo).toFixed(precision) + ' KB'
    } else if (bytes < gigas) {
      return (bytes / megas).toFixed(precision) + ' MB'
    } else if (bytes < teras) {
      return (bytes / gigas).toFixed(precision) + ' GB'
    } else if (bytes < petas) {
      return (bytes / teras).toFixed(precision) + ' TB'
    } else {
      return (bytes / petas).toFixed(precision) + ' PB'
    }
  }

  const handleCancelAll = () => {
    queryClient.cancelMutations()
  }

  const handleDragOver = event => {
    event.preventDefault()
  }

  const handleCheckFiles = (data) => {
    const filesToCheck = data
    if (!filesToCheck?.length) {
      return
    }
    let filesToAdd = []
    const readFileAsync = async file => {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = function () {
          const base64String = reader.result.split(',')[1]
          const md5Hash = MD5(base64String).toString()
          resolve({
            id: generateRandomKey(),
            file: file,
            fileBodyPOST: {
              file_group: 'manifest',
              file_type: 'manifest',
              file_name: file.name,
              file_extension: file.name.split('.').pop(),
              file_size: file.size,
              compression_type: 'None',
              md5: md5Hash,
              data: base64String,
            },
          })
        }
        reader.readAsDataURL(file)
      })
    }
  

    const handleFilesAsync = async () => {
      for (let i = 0; i < filesToCheck?.length; i++) {
        if (!acceptFiles.includes(filesToCheck[i].name.split('.').pop())) return
        const fileToAdd = await readFileAsync(filesToCheck[i])
        await uploadFile(fileToAdd?.fileBodyPOST, {
          onSuccess: uploadedData => {
            if (!isErrorFile) {
              fileToAdd.resource_provider = uploadedData?.resource_provider
              fileToAdd.resource_id = uploadedData?.resource_id
            }
          },
          onError: error => {
            if (error) {
              fileToAdd.error = error
              setShowAlert('shipping-recording:upload-error')
            }
          },
        })
        filesToAdd.push(fileToAdd)
      }
      if (onAdd) onAdd(filesToAdd)
      setFiles([...filesToAdd])
    }
    handleFilesAsync()
  
}

  const handleDrop = event => {
    event.preventDefault()
    if (files?.length + event.dataTransfer.files?.length > maxFiles) {
      return
    }
    for (let i = 0; i < event.dataTransfer.files?.length; i++) {
      if (
        !acceptFiles.includes(event.dataTransfer.files[i].name.split('.').pop())
      ) {
        setShowAlert('shipping-recording:extension-error')
        return
      }
      if (event.dataTransfer.files[i].size > 100000) {
        setShowAlert('shipping-recording:size-error')
        return
      }
    }
    handleCheckFiles([...event.dataTransfer.files])
  }

  const handleOnAdd = event => {
    event.preventDefault()
    if (files?.length + event.target.files?.length > maxFiles) {
      return
    }
    for (let i = 0; i < event.target.files?.length; i++) {
      if (!acceptFiles.includes(event.target.files[i].name.split('.').pop())) {
        setShowAlert('shipping-recording:extension-error')
        return
      }
      if (event.target.files[i].size > 100000) {
        setShowAlert('shipping-recording:size-error')
        return
      }
    }
    handleCheckFiles([...event.target.files])
  }

  const handleOnDelete = id => {
    const newFiles = [...files]
    const index = newFiles.findIndex(element => element.id === id)

    newFiles.splice(index, 1)
    if (onDelete) onDelete(id)
    setFiles(newFiles)
  }

  return (
    <Box
      style={
        !files?.length || isLoadingFile
          ? {
              justifyContent: 'center',
              alignItems: 'center',
              height: height ?? 400,
            }
          : height
          ? { height: height }
          : null
      }
    >
      <Box
        className={classes.dropzone}
        style={
          !files?.length || isLoadingFile
            ? {
                justifyContent: 'center',
                alignItems: 'center',
                height: height ?? 400,
              }
            : height
            ? { height: height }
            : null
        }
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isLoadingFile ? (
          <>
            <CircularProgress size={100} className={classes.loaderIcon} />
            <Typography className={classes.secondaryText}>
              {t('action:uploading-files')}
            </Typography>
            <Typography
              onClick={handleCancelAll}
              className={classes.cancelText}
              variant="h2"
            >
              {t('action:cancel')}
            </Typography>
          </>
        ) : files?.length ? (
          <>
            {files.map((e, index) => (
              <Box key={e?.id} className={classes.rowFile}>
                <Box className={classes.fileBox}>
                  {e?.error ? (
                    <IconError className={classes.errorIcon} />
                  ) : (
                    <IconSuccesful className={classes.successIcon} />
                  )}
                  <Box className={classes.column}>
                    <Box>{e?.file?.name ?? e?.name}</Box>
                    <Box>{transformSize(e?.file?.size ?? e?.size)}</Box>
                  </Box>

                  <IconClose
                    className={classes.closeIcon}
                    onClick={() => {
                      handleOnDelete(e?.id)
                    }}
                  />
                </Box>
              </Box>
            ))}
          </>
        ) : (
          <Box
            className={classes.uploaderBox}
            onClick={() => inputRef.current.click()}
          >
            <IconUpload className={classes.uploaderIcon} />
            <Typography className={classes.primaryText} variant="h2">
              {t('action:upload-files')}
            </Typography>
            <Typography className={classes.secondaryText}>
              {t('action:drag-files')}
            </Typography>
          </Box>
        )}

        <input
          type="file"
          multiple={maxFiles > 1}
          onChange={event => handleOnAdd(event)}
          hidden
          accept={acceptFiles}
          ref={inputRef}
        />
      </Box>
      {files?.length && files?.length < maxFiles ? (
        <Button
          onClick={() => inputRef.current.click()}
          className={classes.uploadButton}
          size={'small'}
        >
          {'Subir Archivo'}
        </Button>
      ) : null}
      {showAlert ? (
        <Alert
          severity="error"
          iconAlignment="top"
          className={classes.errorMessage}
        >
          {t(showAlert)}
        </Alert>
      ) : null}
    </Box>
  )
}

FileUploaderRecording.propTypes = {
  height: PropTypes.number,
  maxFiles: PropTypes.number,
  acceptFiles: PropTypes.string,
  initialFiles: PropTypes.arrayOf(PropTypes.files),
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onError: PropTypes.func,
  onChange: PropTypes.func,
}

export default FileUploaderRecording
